import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import { usePlanUsage } from "@/hooks/use-plan-usage";
import {
  runWorkflow, listWorkflows, getWorkflow, resumeWorkflow,
  analyzeFinancials, getQueue, approveAction, rejectAction
} from "@/services/aiAgentApi";
import {
  Brain, Play, Pause, CheckCircle, XCircle, Clock, AlertTriangle,
  TrendingUp, TrendingDown, Loader2, RefreshCw, ArrowRight, Sparkles, BookOpen
} from "lucide-react";

export default function AIBookkeeperPage() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const businessId = activeBusiness?.id;

  const [activeWorkflowId, setActiveWorkflowId] = useState<number | null>(null);

  // ─── Data fetching ────────────────────────────────────────────────

  const { data: workflows = [], isLoading: wfLoading } = useQuery({
    queryKey: ["/api/ai-agent/workflows", businessId],
    queryFn: () => listWorkflows(businessId),
    enabled: !!businessId,
    refetchInterval: 10000,
  });

  const { data: activeWf } = useQuery({
    queryKey: ["/api/ai-agent/workflow", activeWorkflowId],
    queryFn: () => getWorkflow(activeWorkflowId!),
    enabled: !!activeWorkflowId,
    refetchInterval: activeWorkflowId ? 3000 : false,
  });

  const { data: pendingActions = [] } = useQuery({
    queryKey: ["/api/ai-agent/queue", businessId, "pending"],
    queryFn: () => getQueue(businessId, "pending"),
    enabled: !!businessId,
    refetchInterval: 5000,
  });

  const { data: analysis } = useQuery({
    queryKey: ["/api/ai-agent/analysis", businessId],
    queryFn: () => analyzeFinancials(
      `${new Date().getFullYear()}-01-01`,
      new Date().toISOString().split("T")[0],
      businessId
    ),
    enabled: !!businessId,
    staleTime: 60000,
  });

  // ─── Mutations ────────────────────────────────────────────────────

  const startWorkflow = useMutation({
    mutationFn: async (type: string) => {
      const now = new Date();
      return runWorkflow(type, `${now.getFullYear()}-01-01`, now.toISOString().split("T")[0], businessId);
    },
    onSuccess: (data) => {
      setActiveWorkflowId(data.id);
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agent/workflows"] });
      toast({ title: "Workflow started", description: `${data.workflow_type || "close_books"} is running` });
    },
    onError: (err: any) => toast({ title: "Failed", description: err.message, variant: "destructive" }),
  });

  const approve = useMutation({
    mutationFn: (id: number) => approveAction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agent/queue"] });
      toast({ title: "Action approved and executed" });
    },
    onError: (err: any) => toast({ title: "Approval failed", description: err.message, variant: "destructive" }),
  });

  const reject = useMutation({
    mutationFn: (id: number) => rejectAction(id, "User rejected"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agent/queue"] });
      toast({ title: "Action rejected" });
    },
  });

  const resume = useMutation({
    mutationFn: (id: number) => resumeWorkflow(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agent/workflows"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agent/workflow"] });
      toast({ title: "Workflow resumed" });
    },
  });

  // ─── Helpers ──────────────────────────────────────────────────────

  const statusIcon = (s: string) => {
    switch (s) {
      case "completed": return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "running": return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case "paused": return <Pause className="h-4 w-4 text-yellow-500" />;
      case "failed": return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const statusBadge = (s: string) => {
    const variants: Record<string, string> = {
      completed: "bg-green-100 text-green-800",
      running: "bg-blue-100 text-blue-800",
      paused: "bg-yellow-100 text-yellow-800",
      failed: "bg-red-100 text-red-800",
      pending: "bg-gray-100 text-gray-800",
    };
    return <Badge className={variants[s] || variants.pending}>{s}</Badge>;
  };

  if (!businessId) {
    return <Layout><div className="p-6 text-center text-muted-foreground">Select a business to use AI Bookkeeper.</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg"><Brain className="h-6 w-6 text-primary" /></div>
            <div>
              <h1 className="text-2xl font-bold">AI Bookkeeper</h1>
              <p className="text-muted-foreground text-sm">Automated bookkeeping powered by AI</p>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        {analysis?.success && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Revenue</div>
                <div className="text-2xl font-bold">${(analysis.data?.revenue || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Expenses</div>
                <div className="text-2xl font-bold">${(analysis.data?.expenses || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Net Income</div>
                <div className={`text-2xl font-bold ${(analysis.data?.netIncome || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                  ${(analysis.data?.netIncome || 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Uncategorized</div>
                <div className={`text-2xl font-bold ${(analysis.data?.uncategorizedCount || 0) > 0 ? "text-yellow-600" : "text-green-600"}`}>
                  {analysis.data?.uncategorizedCount || 0}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Run Task */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5" /> Run AI Task</CardTitle>
            <CardDescription>Start an automated bookkeeping workflow</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3">
            <Button onClick={() => startWorkflow.mutate("close_books")} disabled={startWorkflow.isPending} className="gap-2">
              {startWorkflow.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
              Close Books
            </Button>
            <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/ai-agent/analysis"] })} className="gap-2">
              <RefreshCw className="h-4 w-4" /> Refresh Analysis
            </Button>
          </CardContent>
        </Card>

        {/* Active Workflow */}
        {activeWf && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">{statusIcon(activeWf.status)} Workflow: {activeWf.workflow_type}</CardTitle>
                <div className="flex items-center gap-2">
                  {statusBadge(activeWf.status)}
                  {activeWf.status === "paused" && (
                    <Button size="sm" onClick={() => resume.mutate(activeWf.id)} disabled={resume.isPending} className="gap-1">
                      <Play className="h-3 w-3" /> Resume
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(activeWf.steps || []).map((step: any, i: number) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border">
                  <div className="mt-0.5">{statusIcon(step.status)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{step.name.replace(/_/g, " ")}</div>
                    {step.message && <p className="text-xs text-muted-foreground mt-0.5 truncate">{step.message}</p>}
                  </div>
                  {statusBadge(step.status)}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Pending Approvals */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Pending Approvals
              {pendingActions.length > 0 && <Badge variant="destructive">{pendingActions.length}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingActions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No pending approvals</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingActions.map((action: any) => (
                    <TableRow key={action.id}>
                      <TableCell><Badge variant="outline">{action.action_type}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {action.payload?.transaction_id && `Txn #${action.payload.transaction_id}`}
                        {action.payload?.suggested_category_id && ` → Account #${action.payload.suggested_category_id}`}
                      </TableCell>
                      <TableCell className="text-sm">{new Date(action.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="default" onClick={() => approve.mutate(action.id)} disabled={approve.isPending}>
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => reject.mutate(action.id)} disabled={reject.isPending}>
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Insights */}
        {analysis?.insights && analysis.insights.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analysis.insights.map((insight: any, i: number) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                  insight.type === "warning" ? "bg-yellow-50 border-yellow-200" :
                  insight.type === "positive" ? "bg-green-50 border-green-200" :
                  "bg-blue-50 border-blue-200"
                }`}>
                  {insight.type === "warning" ? <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" /> :
                   insight.type === "positive" ? <TrendingUp className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> :
                   <Brain className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />}
                  <span className="text-sm">{insight.message}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Suggestions */}
        {analysis?.suggestions && analysis.suggestions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Suggestions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analysis.suggestions.map((s: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <div className="text-sm font-medium">{s.description}</div>
                  </div>
                  <Badge variant={s.priority === "high" ? "destructive" : s.priority === "medium" ? "default" : "secondary"}>
                    {s.priority}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Recent Workflows */}
        {workflows.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Recent Workflows</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Step</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflows.map((wf: any) => (
                    <TableRow key={wf.id} className="cursor-pointer" onClick={() => setActiveWorkflowId(wf.id)}>
                      <TableCell className="font-medium">{wf.workflow_type}</TableCell>
                      <TableCell>{statusBadge(wf.status)}</TableCell>
                      <TableCell className="text-sm">{wf.current_step?.replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-sm">{new Date(wf.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><ArrowRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
